import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { IdCard, Plus, Trash2, User, Calendar, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Patenti = () => {
  const { user, isSindacatoAdmin, hasPermesso } = useAuth();
  const [patenti, setPatenti] = useState([]);
  const [miePatenti, setMiePatenti] = useState([]);
  const [users, setUsers] = useState([]);
  const [tipiPatente, setTipiPatente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    tipo: '',
    data_scadenza: '',
    note: ''
  });

  const isAdmin = isSindacatoAdmin();
  const canManage = isAdmin || hasPermesso('gestisci_patenti');
  const canView = isAdmin || hasPermesso('visualizza_patenti') || hasPermesso('gestisci_patenti');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Carica le proprie patenti sempre
      const mieRes = await axios.get(`${API_URL}/patenti/mie`);
      setMiePatenti(mieRes.data);

      // Carica tipi patente
      const tipiRes = await axios.get(`${API_URL}/tipi-patente`);
      setTipiPatente(tipiRes.data);

      // Se può vedere tutte le patenti
      if (canView) {
        const patentiRes = await axios.get(`${API_URL}/patenti`);
        setPatenti(patentiRes.data);
      }

      // Se può gestire, carica anche gli utenti
      if (canManage) {
        const usersRes = await axios.get(`${API_URL}/all-users-complete`);
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.user_id || !formData.tipo) {
      toast.error('Seleziona utente e tipo patente');
      return;
    }
    try {
      await axios.post(`${API_URL}/patenti`, formData);
      toast.success('Patente rilasciata');
      setDialogOpen(false);
      setFormData({ user_id: '', tipo: '', data_scadenza: '', note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rilascio');
    }
  };

  const handleRevoke = async (patenteId, tipo) => {
    if (!confirm(`Sei sicuro di voler revocare la patente ${tipo}?`)) return;
    try {
      await axios.delete(`${API_URL}/patenti/${patenteId}`);
      toast.success('Patente revocata');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella revoca');
    }
  };

  const filteredUsers = users.filter(u => 
    u.username.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.nome.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.cognome.toLowerCase().includes(searchUser.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="patenti-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <IdCard className="w-8 h-8" />
            Gestione Patenti
          </h1>
          <p className="text-slate-600 mt-1">
            {canManage ? 'Rilascia e gestisci le patenti' : 'Le tue patenti'}
          </p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Rilascia Patente
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Rilascia Nuova Patente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Cerca Utente</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      placeholder="Cerca per nickname..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Utente</Label>
                  <Select
                    value={formData.user_id}
                    onValueChange={(v) => setFormData({ ...formData, user_id: v })}
                  >
                    <SelectTrigger data-testid="patente-user-select">
                      <SelectValue placeholder="Seleziona utente..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 overflow-y-auto">
                      {filteredUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          @{u.username} - {u.nome} {u.cognome}
                        </SelectItem>
                      ))}
                      {filteredUsers.length === 0 && (
                        <div className="px-2 py-3 text-sm text-slate-500 text-center">
                          {users.length === 0 ? 'Caricamento utenti...' : 'Nessun utente trovato'}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo Patente</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tipiPatente.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data Scadenza (opzionale)</Label>
                  <Input
                    type="date"
                    value={formData.data_scadenza}
                    onChange={(e) => setFormData({ ...formData, data_scadenza: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Note (opzionale)</Label>
                  <Input
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    placeholder="Note aggiuntive..."
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleCreate} className="bg-slate-900 hover:bg-slate-800">
                    Rilascia Patente
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Le mie patenti */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Le Mie Patenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          {miePatenti.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Non possiedi nessuna patente</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {miePatenti.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg"
                >
                  <IdCard className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="font-bold text-blue-900">Patente {p.tipo}</p>
                    <p className="text-xs text-blue-600">
                      Rilasciata: {new Date(p.data_rilascio).toLocaleDateString('it-IT')}
                    </p>
                    {p.data_scadenza && (
                      <p className="text-xs text-blue-600">
                        Scade: {new Date(p.data_scadenza).toLocaleDateString('it-IT')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tutte le patenti (solo per staff) */}
      {canView && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <IdCard className="w-5 h-5" />
              Tutte le Patenti ({patenti.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {patenti.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Nessuna patente rilasciata</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Data Rilascio</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Rilasciata da</TableHead>
                      {canManage && <TableHead>Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patenti.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">@{p.user_username}</p>
                            <p className="text-xs text-slate-500">{p.user_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">{p.tipo}</Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(p.data_rilascio).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell>
                          {p.data_scadenza 
                            ? new Date(p.data_scadenza).toLocaleDateString('it-IT')
                            : <span className="text-slate-400">-</span>
                          }
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {p.rilasciata_da_nome}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRevoke(p.id, p.tipo)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Patenti;
